export default defineAppConfig({
  ui: {
    alert: {
      slots: {
        root: 'relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground',
        title: 'mb-1 font-medium leading-none tracking-tight',
        description: 'text-sm [&_p]:leading-relaxed',
      },
      variants: {
        variant: {
          default: 'bg-background text-foreground',
          destructive: 'border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive',
          success: 'border-success/50 text-success dark:border-success [&>svg]:text-success',
        },
      },
      defaultVariants: {
        variant: 'default' as const,
      },
    },
    alertDialog: {
      slots: {
        content: 'fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
        description: 'text-sm text-muted-foreground',
        title: 'text-lg font-semibold',
        footer: 'flex flex-col-reverse sm:flex-row sm:justify-end sm:gap-x-2',
        header: 'flex flex-col gap-y-2 text-center sm:text-left',
        cancel: 'mt-2 sm:mt-0',
      },
    },
    accordion: {
      slots: {
        content: 'overflow-hidden text-sm transition-all data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up',
        body: 'pb-4 pt-0',
        item: 'border-b',
        trigger: 'flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180',
        trailingIcon: 'h-4 w-4 shrink-0 transition-transform duration-200',
      },
    },
    avatar: {
      slots: {
        root: 'inline-flex items-center justify-center font-normal text-foreground select-none shrink-0 bg-secondary overflow-hidden',
        image: 'h-full w-full object-cover',
      },
      variants: {
        size: {
          sm: 'h-10 w-10 text-xs',
          base: 'h-16 w-16 text-2xl',
          lg: 'h-32 w-32 text-5xl',
        },
        shape: {
          circle: 'rounded-full',
          square: 'rounded-md',
        },
      },
    },
    badge: {
      slots: {
        base: 'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
      },
      variants: {
        variant: {
          default: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
          secondary: 'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
          destructive: 'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
          success: 'border-transparent bg-success text-success-foreground',
          error: 'border-transparent bg-error text-error-foreground',
          outline: 'text-foreground',
        },
      },
      defaultVariants: {
        variant: 'default' as const,
      },
    },
    button: {
      slots: {
        base: 'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
      },
      variants: {
        variant: {
          default: 'bg-primary text-primary-foreground hover:bg-primary/90',
          destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
          success: 'bg-success text-success-foreground hover:bg-success/90',
          outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
          secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
          ghost: 'hover:bg-accent hover:text-accent-foreground',
          link: 'text-primary underline-offset-4 hover:underline',
          orange: 'bg-orange text-orange-foreground shadow-sm hover:bg-orange/90',
          dark: 'bg-dark text-white shadow-sm hover:bg-dark/90',
        },
        size: {
          default: 'h-10 px-4 py-2',
          sm: 'h-9 rounded-md px-3',
          lg: 'h-11 rounded-md px-8',
          xl: 'h-12 rounded-lg px-12',
          icon: 'h-10 w-10',
        },
      },
      defaultVariants: {
        variant: 'default' as const,
        size: 'default' as const,
      },
    },
    calendar: {
      slots: {
        root: 'p-3',
        body: 'mt-4 flex flex-col gap-y-4 sm:flex-row sm:gap-x-4 sm:gap-y-0',
        row: 'mt-2 w-full',
        header: 'relative flex w-full items-center justify-between pt-1',
        heading: 'text-sm font-medium',
        grid: 'w-full border-collapse space-y-1',
        gridRow: 'flex',
        gridWeekDaysRow: '',
        gridBody: '',
        headCell: 'w-9 rounded-md text-[0.8rem] font-normal text-muted-foreground',
        cell: 'relative h-9 w-9 p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([data-selected])]:rounded-md [&:has([data-selected])]:bg-accent [&:has([data-selected][data-outside-view])]:bg-accent/50',
        cellTrigger: [
          'h-9 w-9 p-0 font-normal',
          '[&[data-today]:not([data-selected])]:bg-accent [&[data-today]:not([data-selected])]:text-accent-foreground',
          // Selected
          'data-[selected]:bg-primary data-[selected]:text-primary-foreground data-[selected]:opacity-100 data-[selected]:hover:bg-primary data-[selected]:hover:text-primary-foreground data-[selected]:focus:bg-primary data-[selected]:focus:text-primary-foreground',
          // Disabled
          'data-[disabled]:text-muted-foreground data-[disabled]:opacity-50',
          // Unavailable
          'data-[unavailable]:text-destructive-foreground data-[unavailable]:line-through',
          // Outside months
          'data-[outside-view]:text-muted-foreground data-[outside-view]:opacity-50 [&[data-outside-view][data-selected]]:bg-accent/50 [&[data-outside-view][data-selected]]:text-muted-foreground [&[data-outside-view][data-selected]]:opacity-30',
        ],
        next: 'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        prev: 'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
      },
    },
    card: {
      slots: {
        root: 'rounded-lg border bg-card text-card-foreground shadow-sm',
        header: 'flex flex-col gap-y-1.5 p-6',
        body: 'p-6 pt-0',
        footer: 'flex items-center p-6 pt-0',
        title: 'text-2xl font-semibold leading-none tracking-tight',
        description: 'text-sm text-muted-foreground',
      },
    },
    carousel: {
      slots: {
        root: 'relative',
        viewport: 'overflow-hidden',
        container: 'flex',
        item: 'min-w-0 shrink-0 grow-0 basis-full',
        next: 'touch-manipulation absolute h-8 w-8 rounded-full p-0',
        prev: 'touch-manipulation absolute h-8 w-8 rounded-full p-0',
      },
      variants: {
        orientation: {
          horizontal: {
            container: '-ml-4',
            item: 'pl-4',
            next: '-right-12 top-1/2 -translate-y-1/2',
            prev: '-left-12 top-1/2 -translate-y-1/2',
          },
          vertical: {
            container: '-mt-4 flex-col',
            item: 'pt-4',
            next: '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
            prev: '-top-12 left-1/2 -translate-x-1/2 rotate-90',
          },
        },
      },
    },
    checkbox: {
      slots: {
        root: 'peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
        indicator: 'h-full w-full flex items-center justify-center text-current',
        icon: 'h-4 w-4',
      },
    },
    command: {
      slots: {
        root: 'flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground',
        viewport: 'overflow-hidden p-0 shadow-lg',
        empty: 'py-6 text-center text-sm',
        group: 'overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground',
        groupLabel: 'px-2 py-1.5 text-xs text-muted-foreground font-medium',
        input: 'flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
        item: 'relative flex cursor-default gap-2 select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
        content: 'max-h-[300px] overflow-y-auto overflow-x-hidden',
        separator: '-mx-1 h-px bg-border',
        shortcut: 'ml-auto text-xs tracking-widest text-muted-foreground',
      },
    },
    container: {
      slots: {
        base: 'w-full max-w-[--container] mx-auto px-4 sm:px-6 lg:px-8',
      },
    },
    dialog: {
      slots: {
        overlay: 'fixed inset-0 z-50 data-[state=closed]:animate-out data-[state=open]:animate-in bg-black/80 data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
        content: 'fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
        close: 'absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring',
        description: 'text-sm text-muted-foreground',
        header: 'flex flex-col gap-y-1.5 text-center sm:text-left',
        footer: 'flex flex-col-reverse sm:flex-row sm:justify-end sm:gap-x-2',
        title: 'text-lg font-semibold leading-none tracking-tight',
        scrollContentOverlay: 'fixed inset-0 z-50 grid data-[state=closed]:animate-out data-[state=open]:animate-in place-items-center overflow-y-auto bg-black/80',
        scrollContent: 'relative z-50 grid w-full max-w-lg my-8 gap-4 border border-border bg-background p-6 shadow-lg duration-200 sm:rounded-lg md:w-full',
        scrollContentClose: 'absolute right-3 top-3 rounded-md p-0.5 transition-colors hover:bg-secondary',
      },
    },
    dropdownMenu: {
      slots: {
        content: 'z-50 min-w-32 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        item: 'relative flex cursor-default select-none items-center rounded-sm gap-2 px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50  [&>svg]:size-4 [&>svg]:shrink-0',
        label: 'px-2 py-1.5 text-sm font-semibold',
        checkboxItem: 'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        radioItem: 'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        indicator: 'absolute left-2 h-3.5 w-3.5 flex items-center justify-center',
        separator: '-mx-1 my-1 h-px bg-muted',
        shortcut: 'ml-auto text-xs tracking-widest opacity-60',
        subContent: 'z-50 min-w-32 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        subTrigger: 'flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent data-[state=open]:bg-accent',
        subTriggerIcon: 'ml-auto h-4 w-4',
        trigger: 'outline-none',
      },
    },
    form: {
      slots: {
        item: 'space-y-2',
        description: 'text-sm text-muted-foreground',
        errorMessage: 'text-sm text-destructive font-medium',
      },
    },
    input: {
      slots: {
        root: 'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
      },
    },
    label: {
      slots: {
        root: 'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
      },
    },
    link: {
      slots: {
        base: 'focus-visible:outline-primary',
        variants: {
          active: {
            true: 'text-primary',
            false: [
              'text-muted-foreground hover:text-primary',
              'transition-colors',
            ],
          },
          disabled: {
            true: 'cursor-not-allowed opacity-75',
          },
        },
      },
    },
    navigationMenu: {
      slots: {
        root: 'relative z-10 flex max-w-max flex-1 items-center justify-center',
        list: 'group flex flex-1 list-none items-center justify-center gap-x-1',
        link: 'group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50',
        indicator: 'top-full z-[1] flex h-1.5 items-end justify-center overflow-hidden data-[state=visible]:animate-in data-[state=hidden]:animate-out data-[state=hidden]:fade-out data-[state=visible]:fade-in',
        viewport: 'origin-top-center relative mt-1.5 h-[--reka-navigation-menu-viewport-height] w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90 md:w-[--reka-navigation-menu-viewport-width]',
        viewportWrapper: 'absolute left-0 top-full flex justify-center',
        content: 'left-0 top-0 w-full data-[motion^=from-]:animate-in data-[motion^=to-]:animate-out data-[motion^=from-]:fade-in data-[motion^=to-]:fade-out data-[motion=from-end]:slide-in-from-right-52 data-[motion=from-start]:slide-in-from-left-52 data-[motion=to-end]:slide-out-to-right-52 data-[motion=to-start]:slide-out-to-left-52 md:absolute md:w-auto',
      },
    },
    numberField: {
      slots: {
        root: 'grid gap-1.5',
        base: 'relative [&>[data-slot=input]]:has-[[data-slot=increment]]:pr-5 [&>[data-slot=input]]:has-[[data-slot=decrement]]:pl-5',
        increment: 'absolute top-1/2 -translate-y-1/2 right-0 disabled:cursor-not-allowed disabled:opacity-20 p-3',
        decrement: 'absolute top-1/2 -translate-y-1/2 left-0 p-3 disabled:cursor-not-allowed disabled:opacity-20',
        input: 'flex h-10 w-full rounded-md border border-input bg-background py-2 text-sm text-center ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
      },
    },
    numberTicker: {
      slots: {
        root: 'inline-block tabular-nums text-black dark:text-white tracking-wider',
      },
    },
    pagination: {
      slots: {
        root: 'mx-auto flex w-full justify-center',
        content: 'flex flex-row items-center gap-1',
        ellipsis: 'flex size-9 items-center justify-center',
        first: 'gap-1 px-2.5 sm:pr-2.5',
        last: 'gap-1 px-2.5 sm:pr-2.5',
        next: 'gap-1 px-2.5 sm:pr-2.5',
        previous: 'gap-1 px-2.5 sm:pr-2.5',
      },
    },
    pinInput: {
      slots: {
        root: 'flex gap-2 items-center',
        group: 'flex items-center',
        input: 'relative text-center focus:outline-none focus:ring-2 focus:ring-ring focus:relative focus:z-10 flex h-10 w-10 items-center justify-center border-y border-r border-input text-sm transition-all first:rounded-l-md first:border-l last:rounded-r-md',
      },
    },
    popover: {
      slots: {
        content: 'z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
      },
    },
    progress: {
      slots: {
        root: 'relative h-4 w-full overflow-hidden rounded-full bg-secondary',
        indicator: 'h-full w-full flex-1 bg-primary transition-all',
      },
    },
    radioGroup: {
      slots: {
        root: 'grid gap-2',
        item: 'aspect-square h-4 w-4 rounded-full border border-primary text-primary ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        indicator: 'flex items-center justify-center',
      },
    },
    select: {
      slots: {
        content: 'relative z-50 max-h-96 min-w-32 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        contentPopper: 'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        viewport: 'p-1',
        viewportPopper: 'h-[--reka-select-trigger-height] w-full min-w-[--reka-select-trigger-width]',
        group: 'p-1 w-full',
        item: 'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        itemIndicator: 'absolute left-2 h-3.5 w-3.5 flex items-center justify-center',
        label: 'py-1.5 pl-8 pr-2 text-sm font-semibold',
        scrollDownButton: 'flex cursor-default items-center justify-center py-1',
        scrollUpButton: 'flex cursor-default items-center justify-center py-1',
        separator: '-mx-1 my-1 h-px bg-muted',
        trigger: 'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background data-[placeholder]:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:truncate text-start',
      },
    },
    separator: {
      slots: {
        root: 'shrink-0 bg-border relative',
        label: 'text-xs text-muted-foreground bg-background absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex justify-center items-center',
      },
    },
    sheet: {
      slots: {
        root: 'fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500',
        close: 'absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity disabled:pointer-events-none data-[state=open]:bg-secondary hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring',
        description: 'text-sm text-muted-foreground',
        footer: 'flex flex-col-reverse sm:flex-row sm:justify-end sm:gap-x-2',
        header: 'flex flex-col gap-y-2 text-center sm:text-left',
        title: 'text-lg font-semibold text-foreground',
      },
      variants: {
        side: {
          top: 'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
          bottom: 'inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
          left: 'inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
          right: 'inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm',
        },
      },
      defaultVariants: {
        side: 'right' as const,
      },
    },
    skeleton: {
      slots: {
        root: 'animate-pulse rounded-md bg-muted',
      },
    },
    slider: {
      slots: {
        root: 'relative flex w-full touch-none select-none items-center data-[orientation=vertical]:flex-col data-[orientation=vertical]:w-2 data-[orientation=vertical]:h-full',
        track: 'relative h-2 w-full grow overflow-hidden rounded-full bg-secondary data-[orientation=vertical]:w-2',
        range: 'absolute h-full bg-primary data-[orientation=vertical]:w-full',
        thumb: 'block h-5 w-5 border-2 border-primary rounded-full bg-background ring-offset-background transition-colors disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring',
      },
    },
    switch: {
      slots: {
        root: 'peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input',
        thumb: 'pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5',
      },
    },
    tagsInput: {
      slots: {
        root: 'flex flex-wrap gap-2 items-center rounded-md border border-input bg-background px-3 py-2 text-sm',
        input: 'text-sm min-h-6 focus:outline-none flex-1 bg-transparent px-1',
        item: 'flex h-6 items-center rounded bg-secondary data-[state=active]:ring-ring data-[state=active]:ring-2 data-[state=active]:ring-offset-2 ring-offset-background',
        delete: 'flex rounded bg-transparent mr-1',
        itemText: 'py-1 px-2 text-sm rounded bg-transparent',
      },
    },
    textarea: {
      slots: {
        root: 'flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
      },
    },
    toast: {
      slots: {
        root: 'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[--reka-toast-swipe-end-x] data-[swipe=move]:translate-x-[--reka-toast-swipe-move-x] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full',
        action: 'inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive',
        close: 'absolute right-2 top-2 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600',
        description: 'text-sm opacity-90',
        title: 'text-sm font-semibold',
        viewport: 'fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]',
      },
      variants: {
        variant: {
          default: 'border bg-background text-foreground',
          destructive: 'destructive group border-destructive bg-destructive text-destructive-foreground',
          success: 'success group border-success bg-success text-success-foreground',
        },
      },
      defaultVariants: {
        variant: 'default' as const,
      },
    },
    tooltip: {
      slots: {
        content: 'z-105 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
      },
    },
  },
})
